import { format } from 'date-fns';
import { TattooChoices } from '@prisma/client';
import { capitalize } from '@mui/material';
import {
  Appointment,
  EmployeeToBusiness,
  GeneralRequest,
  LoadedUser,
  ModalData,
  Section,
} from '../../interfaces';
import {
  convertSnakeToTitleCase,
  formatDollarAmount,
  getBooleanText,
  getEmployeeNameFromAppointmentOrGenRequest,
  getFullTattooLocation,
  isNil,
} from '../global/helper';
import {
  getCustomerRelationDetailRows,
  getPrimaryCustomerDetails,
} from './customerModal';
import {
  createReferralSourceSection,
  getAllRefImages,
  getCustomerDetails,
  getCalendarTimezone,
} from '../../helpers/appointments';
import { getAppointmentFlashDesignUrl } from '../../helpers/flashbooks';
import CustomerNameAge from '../../components/appointments/CustomerNameAge';
import { formatOfferedSlotChip } from '../form/offeredSlots';
import ColoredAlert from '../../components/global/ColoredAlert';
import { createServiceDetailsSection } from './appointmentModal';

// Set modal data based on the appointment data
export function createModalData({
  appointment,
  sizeOptions,
  navigateToCustomer,
  allBusinesses,
  employeesToBusiness,
  loadedUser,
}: {
  appointment: Appointment | GeneralRequest;
  sizeOptions: TattooChoices[] | undefined;
  navigateToCustomer: (customerId: string) => void;
  allBusinesses: LoadedUser['allBusinesses'];
  employeesToBusiness?: EmployeeToBusiness[];
  loadedUser?: LoadedUser;
}) {
  const employeeName = getEmployeeNameFromAppointmentOrGenRequest(
    appointment,
    employeesToBusiness,
  );

  // Get boolean variable texts
  const firstTattoo = getBooleanText(appointment.isFirstTattoo);
  const newCustomer = getBooleanText(appointment.isNewCustomer);

  let size = appointment.tattooSize;
  const sizeName = sizeOptions?.find(option => option.value === size);
  if (sizeName) {
    size = sizeName.name;
  }

  const status = (appointment as Appointment).status || '';
  const previousStatus = (appointment as Appointment).previousStatus || '';

  const appointmentBusiness = appointment?.businessId
    ? allBusinesses?.find(b => b.businessId === appointment.businessId)
    : null;

  const customerDetails = getCustomerDetails(appointment);

  const isAcceptedWithOffers =
    status === 'ACCEPTED' &&
    !!(appointment as Appointment).offeredSlots?.length;

  const isTattoo =
    !('serviceId' in appointment) || !(appointment as Appointment).serviceId;
  const service = !isTattoo
    ? loadedUser?.business?.services?.find(
        s => s.serviceId === (appointment as Appointment).serviceId,
      ) ?? null
    : null;
  const piercingLocation =
    service?.piercingLocations?.find(
      l => l.locationId === (appointment as Appointment).piercingLocationId,
    ) ?? null;

  const newModalData = {
    name: 'Booking Request',
    sections: [
      {
        name: 'Overview',
        rows: [
          {
            type: 'TEXT',
            label: 'Status',
            value: [
              (appointment as Appointment).appointmentId
                ? `${capitalize(status.toLowerCase())}${
                    status === 'ARCHIVED' && previousStatus
                      ? ` (${capitalize(previousStatus.toLowerCase())})`
                      : ''
                  }`
                : 'Requested',
            ],
          },
          {
            type: 'TEXT',
            label: 'Requested On:',
            value: [format(new Date(appointment.createdAt!), 'MMMM d, y')],
          },
          (appointment as Appointment).appointmentId &&
          (appointment as Appointment).status === 'ACCEPTED'
            ? {
                type: 'TEXT',
                label: 'Accepted On',
                value: [
                  format(
                    new Date((appointment as Appointment).acceptedOn!),
                    'MMMM d, y',
                  ),
                ],
              }
            : null,
          isAcceptedWithOffers &&
          (appointment as Appointment).offeredTimesDeclinedAt
            ? {
                type: 'NODE',
                label: '',
                value: [
                  <ColoredAlert
                    type="warning"
                    title="None of these times work for the client"
                    subtitle="Offer new times or message the client to sort one out."
                    dismissible={false}
                    animate={false}
                    hideIcon
                  />,
                ],
              }
            : null,
          isAcceptedWithOffers
            ? {
                type: 'TEXT',
                label: 'Times Offered',
                value: (appointment as Appointment).offeredSlots!.map(slot =>
                  formatOfferedSlotChip(
                    slot.startDateTime,
                    (appointment as Appointment).appointmentLengthInMinutes,
                    getCalendarTimezone(
                      loadedUser,
                      (appointment as Appointment).businessTripId,
                    ),
                  ),
                ),
              }
            : null,
          {
            type: 'TEXT',
            label: 'Tattoo Artist',
            value: [employeeName || ''],
          },
          {
            type: 'TEXT',
            label: 'Internal Notes',
            value: [appointment.internalNotes || ''],
          },
          (appointment as Appointment).appointmentId &&
          (appointment as Appointment).status === 'REJECTED' &&
          appointment.rejectionMessage
            ? {
                type: 'TEXT',
                label: 'Rejection Message',
                value: [appointment.rejectionMessage],
              }
            : null,
        ].filter(Boolean),
      },
      {
        name: 'Client',
        rows: [
          {
            type: 'NODE',
            label: 'Name',
            value: [
              <CustomerNameAge
                appointment={appointment}
                typographyVariant="body2"
              />,
            ],
          },
          {
            type: 'TEXT',
            label: 'Booking Location',
            value: [
              (appointment as Appointment).tripCity
                ? (appointment as Appointment).tripCity || ''
                : appointmentBusiness?.name || 'Home Studio',
            ],
          },
          {
            type: 'TEXT',
            label: 'Email',
            value: [customerDetails.email || ''],
          },
          {
            type: 'PHONE',
            label: 'Phone',
            value: [customerDetails.phoneNumber || ''],
          },
          {
            type: 'TEXT',
            label: 'Birthday',
            value: [
              customerDetails.dob
                ? format(
                    new Date(`${customerDetails.dob}T00:00:00`),
                    'MM/dd/yyyy',
                  )
                : '',
            ],
          },
          {
            type: 'TEXT',
            label: 'IG Handle',
            value: [
              !appointment.customerGuest
                ? appointment.customer.igHandle || ''
                : '',
            ],
          },
          {
            type: 'TEXT',
            label: 'Skin Tone',
            value: [appointment.skinTone || ''],
          },
          {
            type: 'TEXT',
            label: 'First Tattoo',
            value: [firstTattoo || ''],
          },
          {
            type: 'TEXT',
            label: 'New Customer',
            value: [newCustomer || ''],
          },
          {
            type: 'TEXT',
            label: 'Internal Notes',
            value: [appointment.customer.internalNotes || ''],
          },
          ...getPrimaryCustomerDetails(customerDetails.primaryCustomer),
          ...getCustomerRelationDetailRows(appointment.customer),
        ],
        buttonText: 'View',
        buttonAction: () => navigateToCustomer(appointment.customerId),
      },
      isTattoo
        ? {
            name: 'Tattoo Details',
            rows: [
              !isNil(appointment.tattooServiceType) && {
                type: 'TEXT',
                label: 'Tattoo Service Type',
                value: [appointment.tattooServiceType || ''],
              },
              'flashDesign' in appointment &&
                appointment.flashDesign &&
                (!!appointment.flashDesign.flashbook?.business ||
                  !!appointment.employee) && {
                  type: 'LINK',
                  label: 'Flash Design',
                  value: [
                    {
                      url: getAppointmentFlashDesignUrl(
                        appointment.flashDesign,
                        appointment.employee,
                      ),
                      label: appointment.flashDesign.name,
                    },
                  ],
                },
              {
                type: 'TEXT',
                label: 'Budget',
                value: [
                  appointment.budget
                    ? formatDollarAmount(appointment.budget)
                    : '',
                ],
              },
              {
                type: 'TEXT',
                label: 'Tattoo Location',
                value: [
                  getFullTattooLocation(
                    appointment.tattooLocation,
                    appointment.bodySide,
                  ),
                ],
              },
              {
                type: 'TEXT',
                label: 'Tattoo Size',
                value: [size || ''],
              },
              {
                type: 'TEXT',
                label: 'Tattoo Color',
                value: [appointment.tattooColor || ''],
              },
              {
                type: 'TEXT',
                label: 'Tattoo Description',
                value: [appointment.tattooDetails || ''],
              },
              !isNil(appointment.otherNotes) && {
                type: 'TEXT',
                label: 'Other Notes',
                value: [appointment.otherNotes || ''],
              },
              !isNil(appointment.ongoingProject) && {
                type: 'TEXT',
                label: 'Ongoing Project',
                value: [getBooleanText(appointment.ongoingProject) || ''],
              },
              !isNil(appointment.coverUp) && {
                type: 'TEXT',
                label: 'Cover Up',
                value: [getBooleanText(appointment.coverUp) || ''],
              },
              !isNil(appointment.surroundingTattoos) && {
                type: 'TEXT',
                label: 'Surrounding Tattoos',
                value: [getBooleanText(appointment.surroundingTattoos) || ''],
              },
              !isNil(appointment.isEphemeral) && {
                type: 'TEXT',
                label: 'Is Ephemeral?',
                value: [getBooleanText(appointment.isEphemeral) || ''],
              },
              {
                type: 'TEXT',
                label: 'General Availability',
                value: [appointment.availability || ''],
              },
            ].filter(Boolean),
          }
        : createServiceDetailsSection({
            appointment,
            service,
            piercingLocation,
          }),
    ] as Section[],
  };

  if ((appointment as Appointment).status === 'ACCEPTED') {
    let priceText = `${formatDollarAmount(
      (appointment as Appointment).price,
      true,
    )}`;
    if (
      (appointment as Appointment).priceType &&
      (appointment as Appointment).priceType === 'RANGE'
    ) {
      priceText = `${formatDollarAmount(
        (appointment as Appointment).minPrice,
        true,
      )} - ${formatDollarAmount((appointment as Appointment).maxPrice, true)}`;
    }
    if ((appointment as Appointment).priceType === 'HOURLY') {
      priceText = `${formatDollarAmount(
        (appointment as Appointment).hourlyPrice,
        true,
      )} per hour`;
    }

    let lengthText = '';
    if (
      appointment &&
      'appointmentLengthInMinutes' in appointment &&
      typeof appointment.appointmentLengthInMinutes === 'number'
    ) {
      const hours = Math.floor(appointment.appointmentLengthInMinutes / 60);
      const minutes = appointment.appointmentLengthInMinutes % 60;

      if (minutes === 0) {
        lengthText = `${hours} hour appointment`;
      } else if (hours === 0) {
        lengthText = `${minutes} minute appointment`;
      } else {
        lengthText = `${hours} hour and ${minutes} minute appointment`;
      }
    }

    newModalData.sections.push({
      name: 'Appointment Details',
      rows: [
        {
          type: 'TEXT',
          label: 'Price Type',
          value: [
            (appointment as Appointment).appointmentId
              ? convertSnakeToTitleCase(
                  (appointment as Appointment).priceType || '',
                )
              : '',
          ],
        },
        {
          type: 'TEXT',
          label: 'Price',
          value: [priceText],
        },
        {
          type: 'TEXT',
          label: 'Appointment Length',
          value: [lengthText || ''],
        },
        {
          type: 'TEXT',
          label: 'Deposit',
          value: [
            (appointment as Appointment).appointmentId &&
            (appointment as Appointment).depositPrice
              ? formatDollarAmount(
                  (appointment as Appointment).depositPrice,
                  true,
                )
              : '',
          ],
        },
      ],
    });
  }

  const refImages = getAllRefImages(appointment);

  newModalData.sections.push({
    name: 'Photos',
    rows: [
      {
        type: 'IMAGE',
        label: 'Reference Images',
        value: refImages,
      },
      {
        type: 'IMAGE',
        label: 'Area Images',
        value: appointment.areaImgsId || [''],
      },
    ],
  });

  const referralSource = appointment.referralSources?.[0];
  if (referralSource) {
    const referralSourceSection = createReferralSourceSection(referralSource);
    if (referralSourceSection) {
      newModalData.sections.push(referralSourceSection);
    }
  }
  return newModalData as ModalData;
}
